import type { UsabilityTier } from '../../lib/types.js';
import { AdditionalityApproachType } from '../../unit-meta/batch.js';
import {
  isCorsiaActual,
  type IssueMetadataCorsiaActual,
  type IssueMetadataCorsiaDefault,
} from '../../unit-meta/issuance.js';
import { ConversionProcess } from '../../unit-meta/shared.js';
import { SustainabilityTier } from '../../unit-meta/shared.js';
import {
  getActualGHGIntensityAirportDelivery,
  getValue,
  tier1Schemes,
  tierCFeedStock,
} from '../constants.js';
import { calculateLcaToBaseline } from './lcaToBaseline.js';

type Meta = IssueMetadataCorsiaActual | IssueMetadataCorsiaDefault;

/**
 * Not tested, because I could lay it with concrete, but it would have no value.
 * Maybe there is good approach to testing it.
 *
 * Good way of thinking in most cases when reading this
 * is that we generally want to give a chance to the issuance to have as highest tier as possible.
 * So we encode all paths that lead to highest tier, then fallback to lower tier if nothing is possible.
 * Some paths are simplified when compared with excel/drawio documentation because that
 * documentation has a lot of duplicated logic and whatnot
 */
const susTierStage1 = (metadata: Meta) => {
  const {
    product,
    batch,
    certification,
  } = metadata;

  const equationResult = isCorsiaActual(metadata)
    ? calculationsCorsia.actual.equations[SustainabilityTier.A](metadata, { applyCCS: false })
    : calculationsCorsia.default.equations[SustainabilityTier.A](metadata, { applyCCS: false });
  const isThresholdReached = calculateLcaToBaseline(metadata, equationResult) >= 0.6;
  const isFeedstockIncludedInEuRedAnnex = product.feedStock.isInEuRed2Annex9PartA;
  const isTierCFeedstock = tierCFeedStock.includes(product.feedStock.name);
  const isSaba = batch.additionalityApproach.type === AdditionalityApproachType.SABA;
  const isFeedstockResidue = product.feedStock.wasCertifiedAsProductionResidue === true;
  const isLowDisplacementConfirmed = certification.isLowDisplacementRiskConfirmed === true;
  const isFeedstockLanduseBased = product.feedStock.isLanduseBasedCropBased === true;
  const isLowIlucRiskConfirmed = certification.isLowILUCRiskConfirmed === true;
  const doesLowRiskFollowUnusedLandApproach = isLowIlucRiskConfirmed && certification.lowRiskFollowUnusedLandApproach === true;
  const isEnergyCrop = product.feedStock.isEnergyCrop === true;
  const isCorsiaDefaultLteZero = product.feedStock.isCorsiaDefaultIlucLcaLteZero === true;

  if (product.conversionProcess === ConversionProcess.PTL) {
    // validated through Zod, should not happen in practice
    throw new Error('Corsia certification scheme does not support PTL conversion processes');
  }

  if (!isSaba || isTierCFeedstock) {
    return susTierStage3(metadata);
  }

  if (!isThresholdReached) {
    // Corsia v4 added hard check at the end of each flow in drawio [GPBOARD-2413]
    // that if threshold is not met then it does not meet requirements for this tier
    // I'm simplifying it by making one big check at the beginning
    return susTierStage2(metadata);
  }

  // check for CCS presence (question r.) is omitted, because it matters only for final calculation, and in reality,

  if (isFeedstockResidue && isLowDisplacementConfirmed && isThresholdReached) {
    return SustainabilityTier.A;
  }

  if (!isFeedstockResidue && !isFeedstockLanduseBased && isThresholdReached) {
    return SustainabilityTier.A;
  }

  if (!isFeedstockResidue && isFeedstockLanduseBased && !isLowIlucRiskConfirmed) {
    return susTierStage3(metadata);
  }

  if (!isFeedstockResidue && isFeedstockLanduseBased && isLowIlucRiskConfirmed && doesLowRiskFollowUnusedLandApproach && isThresholdReached) {
    return SustainabilityTier.A;
  }

  if (!isFeedstockResidue && isFeedstockLanduseBased && isLowIlucRiskConfirmed && !doesLowRiskFollowUnusedLandApproach && !isEnergyCrop && isCorsiaDefaultLteZero && isFeedstockIncludedInEuRedAnnex && isThresholdReached) {
    return SustainabilityTier.A;
  }

  return susTierStage2(metadata);
};

const susTierStage2 = (metadata: Meta): SustainabilityTier => {
  const {
    product,
    certification,
  } = metadata;

  const equationResult = isCorsiaActual(metadata)
    ? calculationsCorsia.actual.equations[SustainabilityTier.B](metadata, { applyCCS: false })
    : calculationsCorsia.default.equations[SustainabilityTier.B](metadata, { applyCCS: false });
  const isThresholdReached = calculateLcaToBaseline(metadata, equationResult) >= 0.6;
  const isFeedstockResidue = product.feedStock.wasCertifiedAsProductionResidue === true;
  const isFeedstockLanduseBased = product.feedStock.isLanduseBasedCropBased === true;
  const isLowIlucRiskConfirmed = certification.isLowILUCRiskConfirmed === true;

  if (!isThresholdReached) {
    // Corsia v4 added hard check at the end of each flow in drawio [GPBOARD-2413]
    // that if threshold is not met then it does not meet requirements for this tier
    // I'm simplifying it by making one big check at the beginning
    return susTierStage3(metadata);
  }

  if (isFeedstockResidue) {
    return SustainabilityTier.B;
  }

  if (!isFeedstockResidue && isFeedstockLanduseBased && isLowIlucRiskConfirmed) {
    return SustainabilityTier.B;
  }

  if (!isFeedstockResidue && !isFeedstockLanduseBased) {
    return SustainabilityTier.B;
  }

  return susTierStage3(metadata);
};

// It always results in C tier but I want to have it explicit
const susTierStage3 = (params: Meta): SustainabilityTier => {
  const {
    batch,
  } = params;

  if (batch.additionalityApproach.type === AdditionalityApproachType.SABA) {
    return SustainabilityTier.C;
  }

  if (batch.additionalityApproach.allowScope3Claims === true) {
    // usability tier -> 2
    // For all SAF certifcates, irrespctive of additionality approach (that is, whether SABA or non-SABA), include the regulatory schemes and the additionality approach that the user selects on the issued SAF certificate
    // For all SAF certificates with non-SABA additionality approach include the following DISCLAIMER: "The SAFc registry did not verify if the addititionality attestations of the SAFc issuer conform to the requirements of the non-SABA additionality approach they inputted"
    return SustainabilityTier.C;
  }

  return SustainabilityTier.C;
};

const usabilityTier = (
  payload: Meta,
  { sustainabilityTier, hasLinkedTree = false, isParentTreeClaimed = false }: { sustainabilityTier: SustainabilityTier; hasLinkedTree?: boolean; isParentTreeClaimed?: boolean },
): UsabilityTier => {
  if (hasLinkedTree || isParentTreeClaimed) {
    // We could distinguish between SAFcA (with hasLinkedTree)
    // and SAFcE (with isParentTreeClaimed) but they are exclusive
    // SAFcA cannot have parentTreeClaimed and SAFcE cannot have linked tree
    return 3;
  }

  const isA = sustainabilityTier === SustainabilityTier.A;
  const isB = sustainabilityTier === SustainabilityTier.B;
  const hasTier1Scheme = payload.batch.regulatorySchemesDeclaration.some(scheme => tier1Schemes.includes(scheme));

  if (isA || isB) {
    return hasTier1Scheme === true ? 1 : 2;
  }

  if (payload.batch.additionalityApproach.type === AdditionalityApproachType.SABA) {
    return hasTier1Scheme === true ? 1 : 2;
  } else {
    const allowScope3Claims = payload.batch.additionalityApproach.allowScope3Claims;

    return allowScope3Claims === true ? 2 : 1;
  }
};

export const calculationsCorsia = {
  actual: {
    sustainabilityTier: susTierStage1,
    usabilityTier,
    equations: {
      [SustainabilityTier.A]: (metadata: IssueMetadataCorsiaActual, params: { applyCCS?: boolean } = {}) => {
        const { greenhouseGas } = metadata;
        const standardEquation = (
          getValue(greenhouseGas.inducedLandUseChange)
          + getValue(greenhouseGas.production)
          + getValue(greenhouseGas.conditioning)
          + getValue(greenhouseGas.processing)
          + getValue(greenhouseGas.transportation)
          + getValue(greenhouseGas.conversionProcesses)
          + getValue(greenhouseGas.fuelTransportation)
          + getValue(greenhouseGas.combustion)
          + getActualGHGIntensityAirportDelivery(greenhouseGas)
          + getValue(greenhouseGas.ghgDisplacementEmissions)
          + getValue(greenhouseGas.emissionsSavingsDueToSoilCarbonSequestration)
        );

        // By default SusTierA applies CCS always unless told not to
        const applyCCS = params.applyCCS ?? true;

        return (
          standardEquation
          - (applyCCS === true ? getValue(greenhouseGas.emissionsSavingsFromCSS) : 0)
        );
      },
      [SustainabilityTier.B]: (metadata: IssueMetadataCorsiaActual, params: { applyCCS?: boolean } = {}) => {
        const { greenhouseGas } = metadata;
        const standardEquation = getValue(greenhouseGas.inducedLandUseChange)
          + getValue(greenhouseGas.production)
          + getValue(greenhouseGas.conditioning)
          + getValue(greenhouseGas.processing)
          + getValue(greenhouseGas.transportation)
          + getValue(greenhouseGas.conversionProcesses)
          + getValue(greenhouseGas.fuelTransportation)
          + getValue(greenhouseGas.combustion)
          + getActualGHGIntensityAirportDelivery(greenhouseGas)
          + getValue(greenhouseGas.emissionsSavingsDueToSoilCarbonSequestration);

        const applyCCS = params.applyCCS ?? calculateLcaToBaseline(metadata, standardEquation) >= 0.6;

        return (
          standardEquation
          - (applyCCS === true ? getValue(greenhouseGas.emissionsSavingsFromCSS) : 0)
        );
      },
      [SustainabilityTier.C]: ({ greenhouseGas }: IssueMetadataCorsiaActual) => {
        return (
          getValue(greenhouseGas.inducedLandUseChange)
          + getValue(greenhouseGas.production)
          + getValue(greenhouseGas.conditioning)
          + getValue(greenhouseGas.processing)
          + getValue(greenhouseGas.transportation)
          + getValue(greenhouseGas.conversionProcesses)
          + getValue(greenhouseGas.fuelTransportation)
          + getValue(greenhouseGas.combustion)
          + getActualGHGIntensityAirportDelivery(greenhouseGas)
          - getValue(greenhouseGas.landfillEmissionsCredits)
          - getValue(greenhouseGas.recyclingEmissionsCredits)
          - getValue(greenhouseGas.emissionsSavingsFromCSS)
        );
      },
    },
  },
  default: {
    sustainabilityTier: susTierStage1,
    usabilityTier,
    equations: {
      [SustainabilityTier.A]: (metadata: IssueMetadataCorsiaDefault, params: { applyCCS?: boolean } = {}) => {
        const { greenhouseGas } = metadata;
        const standardEquation = (
          getValue(greenhouseGas.coreLCAValue)
          + getValue(greenhouseGas.inducedLandUseChange)
          + getValue(greenhouseGas.ghgDisplacementEmissions)
          + getActualGHGIntensityAirportDelivery(greenhouseGas)
        );

        // By default SusTierA applies CCS always unless told not to
        const applyCCS = params.applyCCS ?? true;

        return (
          standardEquation
          - (applyCCS === true ? getValue(greenhouseGas.emissionsSavingsFromCSS) : 0)
        );
      },
      [SustainabilityTier.B]: (metadata: IssueMetadataCorsiaDefault, params: { applyCCS?: boolean } = {}) => {
        const { greenhouseGas } = metadata;
        const standardEquation = getValue(greenhouseGas.coreLCAValue)
          + getValue(greenhouseGas.inducedLandUseChange)
          + getActualGHGIntensityAirportDelivery(greenhouseGas);

        // By default SusTierB applies CCS always unless told not to
        const applyCCS = params.applyCCS ?? true;

        return (
          standardEquation
          - (applyCCS === true ? getValue(greenhouseGas.emissionsSavingsFromCSS) : 0)
        );
      },
      [SustainabilityTier.C]: ({ greenhouseGas }: IssueMetadataCorsiaDefault) => {
        return (
          getValue(greenhouseGas.coreLCAValue)
          + getActualGHGIntensityAirportDelivery(greenhouseGas)
          + getValue(greenhouseGas.inducedLandUseChange)
          - getValue(greenhouseGas.recyclingEmissionsCredits)
          - getValue(greenhouseGas.landfillEmissionsCredits)
          - getValue(greenhouseGas.emissionsSavingsFromCSS)
        );
      },
    },
  },
};
